import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  ICookiesPopup,
  ICurrency,
  ILanguage,
} from '../../../models/strapi.models';
import { fetchStrapi } from '../../../services/api.service';
import { logInfo } from '../../../services/log.service';
import { getStoredData } from '../../../services/storage.service';
import { getAppCurrencies, getDefaultLocale } from '../../../shared/useCommon';
import {
  flatStrapiCollection,
  flatStrapiObject,
} from '../../../shared/useUtils';
import {
  IAppPage,
  setCookiesAcceptance,
  setCookiesPopup,
  setCurrencies,
  setCurrentLocale,
  setLanguages,
  setPages,
} from '../../../store/slices/appSlice';
import { CleanLayoutStyle } from '../../../styles/components/common/layout';
import routes from './../../../scripts/routes.json';
import LoadingOverlay, { ILoadingOverlayProps } from './LoadingOverlay';

interface CleanLayoutProps {
  children: React.ReactNode;
  title: string;
}

const CleanLayout = ({ children, title }: CleanLayoutProps) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.app.isLoading);
  const router = useRouter();
  const pages: IAppPage[] = useMemo(() => routes || [], []);

  const loadingOverlayProps: ILoadingOverlayProps = {
    show: isLoading,
  };

  const setAppLocale = useCallback(
    (languageCode: string) => {
      router.locale = languageCode;
      dispatch(setCurrentLocale(languageCode));
      logInfo([languageCode], 'App → Default locale');
    },
    [router, dispatch]
  );

  const getAppLanguages = useCallback(async () => {
    let urlParams: any = {
      'populate[0]': 'nameTranslations',
      'pagination[start]': '0',
      'pagination[limit]': '-1',
    };

    // Add locales filters to avoid get unnecessary results
    router.locales?.forEach((x, index) => {
      urlParams[`filters[twoLetterIso][$in][${index}]`] = x;
    });

    const languagesResult = await fetchStrapi<any>(`/languages`, urlParams);
    const languages = flatStrapiCollection<ILanguage>(languagesResult);
    dispatch(setLanguages(languages));
    logInfo([languages], 'App → Languages');

    return languages;
  }, [dispatch, router.locales]);

  const getCookiesAcceptance = useCallback(async () => {
    const acceptance = getStoredData('cookies-acceptance') as {
      accepted: boolean;
    };
    dispatch(setCookiesAcceptance(acceptance?.accepted || false));
  }, [dispatch]);

  const getCookiesPopup = useCallback(async () => {
    const cookiesPopupUrlParams = new URLSearchParams({
      locale: router.locale || 'en',
    });
    const cookiesPopup = flatStrapiObject(
      await fetchStrapi<any>(`/cookies-pop-up`, cookiesPopupUrlParams)
    ) as ICookiesPopup;

    dispatch(setCookiesPopup(cookiesPopup));
    logInfo([cookiesPopup], 'App → Cookies popup');
  }, [dispatch, router.locale]);

  const populateAppPages = useCallback(async () => {
    dispatch(setPages(pages));
    logInfo([pages ? pages.length : 0], 'App → App pages count');
  }, [dispatch, pages]);

  useEffect(() => {
    // Populate app pages based on rewrites generated on pre-build
    populateAppPages();

    getAppLanguages().then(() => {
      const defaultLocale = getDefaultLocale(router.locale || 'en');
      setAppLocale(defaultLocale);
    });

    getAppCurrencies().then(async (currencies: ICurrency[]) => {
      dispatch(setCurrencies(currencies));
    });

    getCookiesPopup().then(() => {
      getCookiesAcceptance().then(() => {
        logInfo([], 'App → Cookies pop up generated');
      });
    });
  }, [
    dispatch,
    getAppLanguages,
    getCookiesAcceptance,
    getCookiesPopup,
    populateAppPages,
    router.locale,
    setAppLocale,
  ]);

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <main className={CleanLayoutStyle['clean-layout']}>
        <div className={CleanLayoutStyle['clean-layout-heading']}>
          <div className={CleanLayoutStyle['clean-layout-heading-block']}>
            <Image
              quality={90}
              src={'/assets/igerent-logo-sq.svg'}
              width={40}
              height={40}
              alt="iGerent"
            />
            <h1>{title}</h1>
          </div>
        </div>
        {children}
      </main>
      <LoadingOverlay {...loadingOverlayProps} />
    </>
  );
};

export default CleanLayout;
